import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import type { PassDatedSessionSelectionDTO } from '../../api/models/PassDatedSessionSelectionDTO';
import type { PassOrderPageData } from '../../api/models/PassOrderPageData';
import { DATE_FORMAT } from '../../consts';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { PassDatedSessionPicker } from '../../shared/passDatedSessionPicker/PassDatedSessionPicker';

type Props = {
  setShouldDisableNext: (v: boolean) => void;
};

const PassDatedSessionsStep = ({ setShouldDisableNext }: Props) => {
  const { t } = useTranslation();
  const { orderPageState, setOrderPageState } = useOrderPage();
  const passOrderPage = orderPageState.orderPage as unknown as PassOrderPageData;
  const { account, personalDetails, passDatedSessionSelections, passDatedSessionUnavailable } = orderPageState;
  const [pickerState, setPickerState] = useState({ isLoading: true, isError: false, notEnough: false });

  const startDate = personalDetails.pass_start_date || moment().format(DATE_FORMAT);
  const endDate = moment(startDate, DATE_FORMAT).add(passOrderPage.validity_days, 'days').format(DATE_FORMAT);
  const requiredCount = passOrderPage.num_punches ?? 0;

  useEffect(() => {
    setShouldDisableNext(
      pickerState.isLoading ||
        pickerState.isError ||
        pickerState.notEnough ||
        passDatedSessionSelections.length !== requiredCount,
    );
    return () => setShouldDisableNext(false);
  }, [pickerState, passDatedSessionSelections.length, requiredCount, setShouldDisableNext]);

  const handleChange = (next: PassDatedSessionSelectionDTO[]) =>
    setOrderPageState({
      ...orderPageState,
      passDatedSessionSelections: next,
      passDatedSessionUnavailable: undefined,
    });

  return (
    <Grid container flexDirection="column" gap={1.5}>
      <OrderPageTypography variant="h3" bold>
        {t('pass_dated_sessions.heading')}
      </OrderPageTypography>
      <OrderPageTypography variant="body2">
        {t('pass_dated_sessions.subheading', { count: requiredCount })}
      </OrderPageTypography>
      <OrderPageTypography variant="body2">{t('pass_dated_sessions.changeableLater')}</OrderPageTypography>
      <PassDatedSessionPicker
        account={account}
        passOrderPage={passOrderPage}
        startDate={startDate}
        endDate={endDate}
        value={passDatedSessionSelections}
        onChange={handleChange}
        unavailableSelection={passDatedSessionUnavailable}
        onStateChange={setPickerState}
      />
    </Grid>
  );
};

export default PassDatedSessionsStep;
